
import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import type { Agent } from '../agent-operations.ts';
import styles from './ListingDetails.module.css';

const SELF = '__self__';

export interface AssigneeComboboxProps {
  agents: readonly Agent[];
  viewerUserId: string | null;
  /** Whether the viewer may book for themselves (literal agent role, from the session). */
  selfBookable: boolean;
  value: string | null;
  onChange: (agentUserId: string | null) => void;
  disabled?: boolean;
}

interface Option {
  key: string;
  label: string;
  self: boolean;
}

export function AssigneeCombobox({
  agents,
  viewerUserId,
  selfBookable,
  value,
  onChange,
  disabled = false,
}: AssigneeComboboxProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const controlRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
    flip: boolean;
  } | null>(null);

  const reposition = useCallback(() => {
    const el = controlRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const menuMax = 256;
    const roomBelow = window.innerHeight - r.bottom;
    const flip = roomBelow < menuMax && r.top > roomBelow;
    setMenuPos({ top: flip ? r.top - gap : r.bottom + gap, left: r.left, width: r.width, flip });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    reposition();
    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, { capture: true });
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  const options = useMemo<Option[]>(() => {
    const others: Option[] = agents
      .filter((a) => a.bookable && a.id !== viewerUserId)
      .map((a) => ({ key: a.id, label: a.name, self: false }));
    return selfBookable ? [{ key: SELF, label: 'Myself', self: true }, ...others] : others;
  }, [agents, viewerUserId, selfBookable]);

  const noAgents = options.length === 0;

  // Derived from options so the label and the pickable set can't disagree
  // (a stale value that no longer resolves renders empty, not "Myself").
  const selectedLabel = useMemo(
    () => options.find((o) => (o.self ? value == null : o.key === value))?.label ?? '',
    [value, options],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  function choose(opt: Option) {
    onChange(opt.self ? null : opt.key);
    setQuery('');
    setOpen(false);
  }

  function openList() {
    setQuery('');
    setActive(0);
    setOpen(true);
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      openList();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[active];
      if (opt) choose(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      setOpen(false);
    }
  };

  const display = open ? query : selectedLabel;

  return (
    <div className={styles.assignee}>
      <label id={`${listId}-label`} className={styles.assigneeLabel} htmlFor={`${listId}-input`}>
        On behalf of
      </label>
      <div className={styles.assigneeControl} ref={controlRef}>
        <Input
          id={`${listId}-input`}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-labelledby={`${listId}-label`}
          autoComplete="off"
          disabled={disabled}
          className={styles.assigneeInput}
          placeholder={noAgents ? 'No agents yet' : 'Search agents…'}
          value={display}
          onFocus={openList}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            setTimeout(() => {
              setOpen(false);
              setQuery('');
            }, 120);
          }}
        />
        <span className={styles.assigneeCaret} aria-hidden="true">
          <CaretGlyph />
        </span>

        {open &&
          menuPos &&
          createPortal(
            <ul
              id={listId}
              role="listbox"
              aria-label="On behalf of"
              className={styles.assigneeList}
              style={{
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                transform: menuPos.flip ? 'translateY(-100%)' : undefined,
              }}
            >
              {filtered.length > 0 ? (
                filtered.map((o, i) => {
                  const isSelected = o.self ? value == null : o.key === value;
                  return (
                    <li
                      key={o.key}
                      role="option"
                      aria-selected={isSelected}
                      className={cx(
                        styles.assigneeOption,
                        i === active && styles.assigneeOptionActive,
                      )}
                      onMouseEnter={() => setActive(i)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        choose(o);
                      }}
                    >
                      <span className={cx(o.self && styles.assigneeSelfLabel)}>{o.label}</span>
                      {isSelected && (
                        <span className={styles.assigneeCheck} aria-hidden="true">
                          <CheckGlyph />
                        </span>
                      )}
                    </li>
                  );
                })
              ) : (
                <li className={styles.assigneeEmpty} aria-disabled="true">
                  {noAgents
                    ? 'No agents yet — add agents from Users.'
                    : `No agents match “${query.trim()}”.`}
                </li>
              )}
            </ul>,
            document.body,
          )}
      </div>
    </div>
  );
}

function CaretGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.5 8.5l2.8 2.8 6.2-6.6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
